// src/aiConfig.js
// Central AI provider configuration manager.
// All provider settings are persisted in localStorage so they survive page reloads.
import { authFetch } from './api';

export const PROVIDERS = {
  anthropic: {
    id:       'anthropic',
    name:     'Anthropic Claude',
    icon:     '🟠',
    color:    '#D97706',
    bg:       '#FFFBEB',
    border:   '#FDE68A',
    docsUrl:  'https://console.anthropic.com/settings/keys',
    models: [
      { id:'claude-opus-4-5',    label:'Claude Opus 4.5 (Most capable)' },
      { id:'claude-sonnet-4-5',  label:'Claude Sonnet 4.5 (Recommended)' },
      { id:'claude-haiku-4-5-20251001', label:'Claude Haiku 4.5 (Fastest)' },
    ],
    defaultModel: 'claude-sonnet-4-5',
    keyPrefix: 'sk-ant-',
    keyHint:   'sk-ant-api03-…',
  },
  openai: {
    id:      'openai',
    name:    'OpenAI GPT',
    icon:    '🟢',
    color:   '#059669',
    bg:      '#ECFDF5',
    border:  '#A7F3D0',
    docsUrl: 'https://platform.openai.com/api-keys',
    models: [
      { id:'gpt-4o',          label:'GPT-4o (Recommended)' },
      { id:'gpt-4o-mini',     label:'GPT-4o Mini (Faster / cheaper)' },
      { id:'gpt-4-turbo',     label:'GPT-4 Turbo' },
      { id:'gpt-3.5-turbo',   label:'GPT-3.5 Turbo (Fastest)' },
    ],
    defaultModel: 'gpt-4o',
    keyPrefix: 'sk-',
    keyHint:   'sk-proj-…',
  },
  gemini: {
    id:      'gemini',
    name:    'Google Gemini',
    icon:    '🔵',
    color:   '#1D4ED8',
    bg:      '#EFF6FF',
    border:  '#BFDBFE',
    docsUrl: 'https://aistudio.google.com/app/apikey',
    models: [
      { id:'gemini-2.0-flash',         label:'Gemini 2.0 Flash (Recommended)' },
      { id:'gemini-2.0-flash-lite',     label:'Gemini 2.0 Flash Lite (Fastest)' },
      { id:'gemini-1.5-pro',           label:'Gemini 1.5 Pro (Most capable)' },
      { id:'gemini-1.5-flash',         label:'Gemini 1.5 Flash' },
    ],
    defaultModel: 'gemini-2.0-flash',
    keyPrefix: 'AIza',
    keyHint:   'AIzaSy…',
  },
};

export type ProviderId = keyof typeof PROVIDERS;

export interface AIConfig {
  provider: ProviderId;
  models: Record<ProviderId, string>;
  temperature: number;
  maxTokens: number;
}

export interface AIMessage {
  role: string;
  content: string;
}

const STORAGE_KEY = 'hms_ai_config';

export function loadConfig(): AIConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      // Strip any stale keys/useBackend from a config saved before those
      // fields were removed (see the security note below) rather than
      // trusting whatever shape localStorage happens to hold.
      const parsed = JSON.parse(raw);
      return {
        provider: parsed.provider || 'openai',
        models: parsed.models || { anthropic: 'claude-sonnet-4-5', openai: 'gpt-4o', gemini: 'gemini-2.0-flash' },
        temperature: parsed.temperature ?? 0.3,
        maxTokens: parsed.maxTokens ?? 1500,
      };
    }
  } catch {}
  // Default config
  return {
    provider:    'openai',
    models:      { anthropic:'claude-sonnet-4-5', openai:'gpt-4o', gemini:'gemini-2.0-flash' },
    temperature: 0.3,
    maxTokens:   1500,
  };
}

export function saveConfig(cfg: AIConfig): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
}

export function getActiveProvider(cfg: AIConfig) {
  return PROVIDERS[cfg.provider] || PROVIDERS.anthropic;
}

export function getActiveModel(cfg: AIConfig): string {
  return cfg.models?.[cfg.provider] || PROVIDERS[cfg.provider]?.defaultModel;
}

// ── Test connection ─────────────────────────────────────────────
// Routes through the backend's own POST /api/ai/test — every AI call in
// this app does, without exception, since removing the direct-from-browser
// call path (see git history / ISS-05 in the product assessment): API keys
// live only in the backend's .env, never in browser localStorage, and every
// call — including this test — goes through the tenant AI policy switch and
// the audit log in routes/ai.js.
export async function testConnection(cfg: AIConfig): Promise<boolean> {
  const res = await authFetch('/api/ai/test', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider: cfg.provider, model: getActiveModel(cfg) }),
  });
  const data = await res.json();
  if (!res.ok || !data.ok) throw new Error(data.error || 'Connection test failed');
  return true;
}
